/**
 * Density preference (compact | comfortable).
 * Applied as data-density on <html>. Persisted in localStorage.
 * Components consume via attribute selectors when they need to vary padding.
 */

import { useCallback, useEffect, useState } from 'react';

export type Density = 'compact' | 'comfortable';

const STORAGE_KEY = 'bolao2026:density';
const DEFAULT_DENSITY: Density = 'comfortable';
const HTML_ATTRIBUTE = 'data-density';

function readStoredDensity(): Density {
  if (typeof window === 'undefined') return DEFAULT_DENSITY;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'compact' || raw === 'comfortable' ? raw : DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

function applyDensity(density: Density): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(HTML_ATTRIBUTE, density);
}

/**
 * Initialize density on first paint. Call once from main.tsx before render.
 * Idempotent.
 */
export function initDensity(): void {
  applyDensity(readStoredDensity());
}

export function useDensity(): {
  density: Density;
  setDensity: (next: Density) => void;
  toggleDensity: () => void;
} {
  const [density, setDensityState] = useState<Density>(readStoredDensity);

  useEffect(() => {
    applyDensity(density);
    try {
      window.localStorage.setItem(STORAGE_KEY, density);
    } catch {
      /* storage may be disabled — non-fatal */
    }
  }, [density]);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
  }, []);

  const toggleDensity = useCallback(() => {
    setDensityState((prev) => (prev === 'compact' ? 'comfortable' : 'compact'));
  }, []);

  return { density, setDensity, toggleDensity };
}
